import store from 'redux/store';
import { waitDialog } from 'dialogs';
import { recordToEdit } from 'edit/recordToEdit';
import type { CreateTarget } from 'actions';
import { getFolder } from 'api/folders/endpoints/getFolder';
import type { Folder } from 'redux/slices/lists/foldersSlice';
import EditFolderModal from '../EditFolderModal';

export type ShowEditFolderArgs = { readonly mode: 'create'; readonly createIn?: CreateTarget } | { readonly mode: 'edit'; readonly id: number };

// The modal takes the full record inline. Prefer the lists the store already
// holds — the paginated folders plus the children of any expanded row (a child
// row carries every Folder field except parent_folder_name). A child row is
// completed rather than cast.
const findFolder = (id: number): Folder | null => {
  const { folders, childFolders } = store.getState().listOfFolders;
  const top = folders.find((folder) => folder.folder_id === id);
  if (top) return top;
  const child = Object.values(childFolders)
    .flat()
    .find((folder) => folder.folder_id === id);
  return child ? { ...child, parent_folder_name: null } : null;
};

/**
 * Open the folder create / edit modal and resolve when it closes (so the caller
 * can refresh). A standalone async helper: for edit it ensures the record is
 * present — from the list slice, else fetched by id — so it works when launched
 * from the Workspace (which never populates that slice); the fetch is a single
 * object, not the whole list (the folder may not be on the first page).
 */
export const showEditFolder = async (args: ShowEditFolderArgs): Promise<void> => {
  const createIn = args.mode === 'create' ? args.createIn : undefined;
  let data: Folder | null = null;
  if (args.mode === 'edit') {
    const fromSlice = findFolder(args.id);
    if (fromSlice) data = fromSlice;
    else {
      const record = await recordToEdit('Folder', () => getFolder(args.id));
      if (record === undefined) return;
      data = record;
    }
  }
  await waitDialog<void>((ok) => <EditFolderModal show handleClose={ok} mode={args.mode} data={data} createIn={createIn} />);
};
